import { FulfillmentTypeKey } from '@carvana/merch-contracts/FulfillmentType';
import { useEffectWithController } from '@carvana/merch-react-hooks';
import { isSuccess } from '@carvana/merch-result';
import { useContext, useRef } from 'react';
import { useResolvedOnServer } from '~/context/common/DeferredResolutionContext';
import StaticContext, {
  useEventCorrelationId
} from '~/context/common/StaticContext';
import { useUserId, useUserLocation } from '~/context/common/UserContext';
import DeliveryPickupContext from '~/context/vdp/DeliveryPickupContext';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import useSkipFirstFetchIfResolvedOnServer from '~/hooks/common/useSkipFirstFetchIfResolvedOnServer';
import useCanShowDeliveryAndPickupInfo from '~/hooks/vdp/selectors/useCanShowDeliveryAndPickupInfo';
import fetchVehiclesDeliveryPickup from '~/services/common/fetchVehiclesDeliveryPickup';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useGetDeliveryPickup() {
  const {
    deliveryPickup: { setData, setError, setLoading },
    fetchDeliveryPickupFlag
  } = useContext(DeliveryPickupContext);
  const { clientId } = useContext(StaticContext);
  const eventCorrelationId = useEventCorrelationId();
  const {
    fulfillmentType = FulfillmentTypeKey.Unknown,
    location: { locationId = 0 } = {},
    saleStatus = '',
    vehicleId = 0
  } = useGetVehicleDetails();
  const lastFetchFlag = useRef(fetchDeliveryPickupFlag);
  const { deliveryPickup: deliveryPickupResolvedOnServer } =
    useResolvedOnServer();
  const userId = useUserId();
  const { zip } = useUserLocation();

  const shouldSkipFetch = useSkipFirstFetchIfResolvedOnServer(
    deliveryPickupResolvedOnServer
  );
  const canShowDeliveryAndPickupInfo = useCanShowDeliveryAndPickupInfo();

  useEffectWithController(
    controller => {
      if (!canShowDeliveryAndPickupInfo || !zip) return;

      // A trigger bump (server-miss fallback) must fetch even inside the skip window, else a
      // miss firing before availability loads is lost when the skip is later consumed.
      const wasTriggered = fetchDeliveryPickupFlag !== lastFetchFlag.current;
      lastFetchFlag.current = fetchDeliveryPickupFlag;

      // Consumed after the guard above so the skip lands on the availability-ready
      // run (the first run canShowDeliveryAndPickupInfo allows), not on mount.
      if (shouldSkipFetch() && !wasTriggered) return;

      setLoading();

      fetchVehiclesDeliveryPickup(
        {
          clientId,
          correlationId: eventCorrelationId,
          userId,
          vehicles: [
            {
              vehicleInventoryType: fulfillmentType,
              vehicleLocationId: locationId,
              vehicleSaleStatus: saleStatus,
              vehicleId
            }
          ],
          zip
        },
        controller
      )
        .then(({ data }) => {
          if (!isSuccess(data)) {
            throw new Error(data.errorMessage);
          }

          if (data.content.length === 0) {
            throw new Error('No content found');
          }

          setData(data.content[0]);
        })
        .catch(
          generateErrorLogger(
            'Get delivery & pickup info',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      canShowDeliveryAndPickupInfo,
      clientId,
      eventCorrelationId,
      fetchDeliveryPickupFlag,
      fulfillmentType,
      locationId,
      saleStatus,
      setData,
      setError,
      setLoading,
      shouldSkipFetch,
      userId,
      vehicleId,
      zip
    ]
  );
}
